import '../home/home.scss';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import msiaLogo from '../../assets/images/logo160.png';
import capitalImage from '../../assets/images/missouri_capital.png';
import circleArrow from '../../assets/icons/circle_arrow.png';
import EventsSchedule from '../../components/event-schedule/event-schedule';
import sponsorData from '../../sponsor-items/sponsors.json';

const RECENT_SPONSOR_FALLBACK_TIERS = ['Platinum', 'Site Supporter'];
// Recent sponsors favor Gold/Silver/Bronze tiers, newest-first within each tier, falling back to Platinum/Site Supporter only to fill remaining slots.
const priorityRecentSponsors = sponsorData.tiers
    .filter((tier) => !RECENT_SPONSOR_FALLBACK_TIERS.includes(tier.name))
    .flatMap((tier) => [...tier.sponsors].reverse());
const fallbackRecentSponsors = sponsorData.tiers
    .filter((tier) => RECENT_SPONSOR_FALLBACK_TIERS.includes(tier.name))
    .flatMap((tier) => [...tier.sponsors].reverse());
const recentSponsors = [...priorityRecentSponsors, ...fallbackRecentSponsors].slice(0, 4);
const platinumSponsors = sponsorData.tiers.find((tier) => tier.name === 'Platinum')?.sponsors ?? [];
const siteSponsors = sponsorData.tiers.find((tier) => tier.name === 'Site Supporter')?.sponsors ?? [];

const sponsorAnchor = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const sponsorHref = (tierName: string, sponsorName: string) =>
    `/sponsors#${sponsorAnchor(tierName)}-${sponsorAnchor(sponsorName)}`;
const hasValidLogo = (logo?: string) => typeof logo === 'string' && logo.trim().length > 0;

type SponsorNameProps = {
    name: string;
    abbreviation?: string;
};

const SponsorName = ({ name, abbreviation }: SponsorNameProps) => {
    const measureRef = useRef<HTMLSpanElement>(null);
    const [useAbbreviation, setUseAbbreviation] = useState(false);

    useEffect(() => {
        const measureName = () => {
            const element = measureRef.current;
            if (!element || !abbreviation) {
                return;
            }

            const lineHeight = parseFloat(getComputedStyle(element).lineHeight);
            setUseAbbreviation(element.scrollHeight > lineHeight * 2 + 1);
        };

        measureName();
        const resizeObserver = new ResizeObserver(measureName);
        if (measureRef.current) {
            resizeObserver.observe(measureRef.current);
        }

        return () => resizeObserver.disconnect();
    }, [abbreviation, name]);

    return (
        <span className="hero-sponsors__name" title={name}>
            <span ref={measureRef} className="hero-sponsors__name-measure" aria-hidden="true">{name}</span>
            <span>{useAbbreviation && abbreviation ? abbreviation : name}</span>
        </span>
    );
};

const Home = () => {
    return (
        <>
            <div className="page-content">
                <img src={capitalImage} alt="Missouri Capitol" className="page-background" />
                <div className="page-sections">
                    <div className="mission-box">
                        <h1 className="mission-title">
                            Together We <br/>
                            Promote and Protect <br/>
                            the Interests of <br/> 
                            Self-Insurers.
                        </h1>
                        <p className="mission-subtitle">MSIA Mission #1</p>
                        <img src={msiaLogo} alt="MSIA Logo" className="mission-logo" />
                    </div>
                    <aside className="hero-sponsors" aria-label="Featured sponsors">
                        <div className="hero-sponsors__top-row">
                            <div className="hero-sponsors__left-column">
                                <details className="hero-sponsors__platinum" open>
                                    <summary>Site Sponsors</summary>
                                    <div className="hero-sponsors__platinum-list">
                                        {siteSponsors.map((sponsor) => (
                                            <Link
                                                key={sponsor.name}
                                                to={sponsorHref('Site Supporter', sponsor.name)}
                                                className="hero-sponsors__platinum-link"
                                            >
                                                {hasValidLogo(sponsor.logo) ? (
                                                    <img src={require(`../../sponsor-items/${sponsor.logo}`)} alt="" />
                                                ) : (
                                                    <span className="hero-sponsors__logo-placeholder">Logo</span>
                                                )}
                                                <SponsorName name={sponsor.name} abbreviation={sponsor.abbreviation} />
                                            </Link>
                                        ))}
                                    </div>
                                </details>

                                <details className="hero-sponsors__platinum" open>
                                    <summary>Platinum Sponsors</summary>
                                    <div className="hero-sponsors__platinum-list">
                                        {platinumSponsors.map((sponsor) => (
                                            <Link
                                                key={sponsor.name}
                                                to={sponsorHref('Platinum', sponsor.name)}
                                                className="hero-sponsors__platinum-link"
                                            >
                                                {hasValidLogo(sponsor.logo) ? (
                                                    <img src={require(`../../sponsor-items/${sponsor.logo}`)} alt="" />
                                                ) : (
                                                    <span className="hero-sponsors__logo-placeholder">Logo</span>
                                                )}
                                                <SponsorName name={sponsor.name} abbreviation={sponsor.abbreviation} />
                                            </Link>
                                        ))}
                                    </div>
                                </details>
                            </div>

                            <div className="hero-sponsors__recent">
                                <div className="hero-sponsors__recent-heading">
                                    <span className="hero-sponsors__recent-title">Recent Sponsors</span>
                                </div>
                                <div className="hero-sponsors__recent-list">
                                    {recentSponsors.map((sponsor) => {
                                        const tier = sponsorData.tiers.find((item) => item.sponsors.some((itemSponsor) => itemSponsor.name === sponsor.name));
                                        return (
                                        <Link
                                            key={sponsor.name}
                                            to={sponsorHref(tier?.name ?? '', sponsor.name)}
                                            className="hero-sponsors__recent-link"
                                        >
                                            <SponsorName name={sponsor.name} abbreviation={sponsor.abbreviation} />
                                        </Link>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                    </aside>
                </div>
            </div>

            <section className="membership-features">
                <div className="features-inner">
                    <h2>Membership Features</h2>
                    <h6>
                        <strong>Education / Legislation / Industry Info</strong>
                    </h6>
                    <div className="feature-grid">
                        <article className="feature-card">
                            <div className="feature-card__heading">
                                <div className="feature-icon">📜</div>
                                <h3>Legislative <br/> Reports</h3>
                            </div>
                            <p>Learn of current and past efforts to protect our industry.</p>
                            <span className="feature-link">
                                <img src={circleArrow} alt="Learn more" />
                            </span>
                        </article>
                        <article className="feature-card">
                            <div className="feature-card__heading">
                                <div className="feature-icon">👥</div>
                                <h3>Membership <br/> Directory</h3>
                            </div>
                            <p>Search the membership directory for connections that can help.</p>
                            <span className="feature-link">
                                <img src={circleArrow} alt="Learn more" />
                            </span>
                        </article>
                        <article className="feature-card">
                            <div className="feature-card__heading">
                                <div className="feature-icon">👍</div>
                                <h3>Networking <br/> Resources</h3>
                            </div>
                            <p>Connect with members through exclusive networking resources and opportunities.</p>
                            <span className="feature-link">
                                <img src={circleArrow} alt="Learn more" />
                            </span>
                        </article>
                        <article className="feature-card">
                            <div className="feature-card__heading">
                                <div className="feature-icon">📍</div>
                                <h3>Regional <br/> Meetings</h3>
                            </div>
                            <p>Join regional gatherings to connect with peers and stay engaged in local MSIA conversations.</p>
                            <span className="feature-link">
                                <img src={circleArrow} alt="Learn more" />
                            </span>
                        </article>
                        <article className="feature-card">
                            <div className="feature-card__heading">
                                <div className="feature-icon">🔍</div>
                                <h3>Industry <br/> Insights</h3>
                            </div>
                            <p>Stay informed with timely insights and trends affecting self-insurers. Gain access to member-only news and updates.</p>
                            <span className="feature-link">
                                <img src={circleArrow} alt="Learn more" />
                            </span>
                        </article>
                        <article className="feature-card">
                            <div className="feature-card__heading">
                                <div className="feature-icon">🎓</div>
                                <h3>Education <br/> Opportunities</h3>
                            </div>
                            <p>Access programs and resources that support continuing education for self-insurance professionals.</p>
                            <span className="feature-link">
                                <img src={circleArrow} alt="Learn more" />
                            </span>
                        </article>
                    </div>

                    <Link to="/membership-document" className="register-now-card">
                        <div className="register-copy">
                            <h2>Register Now</h2>
                            <p>Become a member today</p>
                        </div>
                    </Link>
                </div>
            </section>

            <section className="upcoming-meetings-section">
                <div className="section-inner">
                    <div className="section-header">
                        <div>
                            <p className="eyebrow">Upcoming Events</p>
                            <h2 className="upcoming-section-title">This Week's Events</h2>
                        </div>
                        <Link to="/schedule" className="section-link">View schedule</Link>
                    </div>
                    <EventsSchedule compactHomePreview />
                </div>
            </section>
        </>
    );
};

export default Home;
